import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  FormControlLabel,
  Switch,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
} from "@mui/material";
import Add from "@mui/icons-material/Add";
import Delete from "@mui/icons-material/Delete";
import { BRAND_COLOR, BRAND_HOVER } from "@kivunova/kivufrontendcommon";
import { BUSINESS_TYPE_OPTIONS } from "../common/adminEnumLabels";
import {
  ADMIN_MARKET_COUNTRIES,
  currencyForAdminCountry,
} from "../common/adminMarketCountries";
import { PlanFormData } from "./types/planTypes";
import { useCreatePlan, useCreatePlanPricing } from "../../hooks/api/usePlan";
import { useGetBillingCycleList } from "../../hooks/api/useBillingCycle";
import {
  useGetFeatureList,
  useAddPlanFeature,
} from "../../hooks/api/useFeature";
import type { BillingCycle } from "../../Graphql/types/billingCycle";
import type { Feature } from "../../Graphql/types/feature";

interface PendingPricing {
  billingCycleId: string;
  billingCycleCode: string;
  countryCode: string;
  priceAmount: number;
  priceCurrency: string;
}

interface PendingFeature {
  featureCode: string;
  name: string;
}

interface AddPlanModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const AddPlanModal: React.FC<AddPlanModalProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const [formData, setFormData] = useState<PlanFormData>({
    name: "",
    description: "",
    isActive: true,
    businessType: "",
  });
  const [billingCycles, setBillingCycles] = useState<BillingCycle[]>([]);
  const [allFeatures, setAllFeatures] = useState<Feature[]>([]);
  const [pendingPricings, setPendingPricings] = useState<PendingPricing[]>([]);
  const [pendingFeatures, setPendingFeatures] = useState<PendingFeature[]>([]);
  const [newPricingBillingCycleId, setNewPricingBillingCycleId] = useState("");
  const [newPricingCountryCode, setNewPricingCountryCode] = useState("RW");
  const [newPricingAmount, setNewPricingAmount] = useState("");
  const [newPricingCurrency, setNewPricingCurrency] = useState("RWF");
  const [newFeatureCode, setNewFeatureCode] = useState("");
  /** Set after plan create succeeds so pricing/feature retries do not create another plan. */
  const [createdPlanId, setCreatedPlanId] = useState<string | null>(null);

  const createPlan = useCreatePlan();
  const createPlanPricing = useCreatePlanPricing();
  const addPlanFeature = useAddPlanFeature();
  const getBillingCycleList = useGetBillingCycleList();
  const getFeatureList = useGetFeatureList();

  useEffect(() => {
    if (open) {
      getBillingCycleList
        .mutateAsync({ activeOnly: false })
        .then((res) =>
          setBillingCycles(res.listBillingCycles?.billingCycleList ?? []),
        )
        .catch(() => {});
      getFeatureList
        .mutateAsync({ activeOnly: false })
        .then((res) => setAllFeatures(res.listFeatures?.featureList ?? []))
        .catch(() => {});
    }
  }, [open]);

  const handleChange =
    (field: keyof PlanFormData) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = field === "isActive" ? e.target.checked : e.target.value;
      setFormData((prev) => ({ ...prev, [field]: value }));
    };

  const handleCountryChange = (code: string) => {
    setNewPricingCountryCode(code);
    setNewPricingCurrency(currencyForAdminCountry(code));
  };

  const handleAddPricing = () => {
    if (
      !newPricingBillingCycleId ||
      !newPricingCountryCode ||
      !newPricingAmount.trim()
    )
      return;
    const amount = parseFloat(newPricingAmount);
    if (Number.isNaN(amount)) return;
    const bc = billingCycles.find(
      (c) => c.billingCycleId === newPricingBillingCycleId,
    );
    if (!bc) return;
    const alreadyPending = pendingPricings.some(
      (p) =>
        p.billingCycleId === bc.billingCycleId &&
        p.countryCode === newPricingCountryCode,
    );
    if (alreadyPending) return;
    setPendingPricings((prev) => [
      ...prev,
      {
        billingCycleId: bc.billingCycleId,
        billingCycleCode: bc.code,
        countryCode: newPricingCountryCode,
        priceAmount: amount,
        priceCurrency:
          newPricingCurrency || currencyForAdminCountry(newPricingCountryCode),
      },
    ]);
    setNewPricingBillingCycleId("");
    setNewPricingAmount("");
  };

  const handleRemovePricing = (billingCycleId: string, countryCode: string) => {
    setPendingPricings((prev) =>
      prev.filter(
        (p) =>
          !(
            p.billingCycleId === billingCycleId && p.countryCode === countryCode
          ),
      ),
    );
  };

  const handleAddFeature = () => {
    if (!newFeatureCode) return;
    const f = allFeatures.find((x) => x.featureCode === newFeatureCode);
    if (!f) return;
    if (pendingFeatures.some((x) => x.featureCode === newFeatureCode)) return;
    setPendingFeatures((prev) => [
      ...prev,
      { featureCode: f.featureCode, name: f.name },
    ]);
    setNewFeatureCode("");
  };

  const handleRemoveFeature = (featureCode: string) => {
    setPendingFeatures((prev) =>
      prev.filter((p) => p.featureCode !== featureCode),
    );
  };

  const featuresNotAdded = allFeatures.filter(
    (f) => !pendingFeatures.some((p) => p.featureCode === f.featureCode),
  );

  const canAddMorePricing = billingCycles.some((bc) =>
    ADMIN_MARKET_COUNTRIES.some(
      (c) =>
        !pendingPricings.some(
          (p) =>
            p.billingCycleId === bc.billingCycleId && p.countryCode === c.code,
        ),
    ),
  );

  const countriesAvailableForSelectedCycle = ADMIN_MARKET_COUNTRIES.filter(
    (c) =>
      !pendingPricings.some(
        (p) =>
          p.billingCycleId === newPricingBillingCycleId &&
          p.countryCode === c.code,
      ),
  );

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      isActive: true,
      businessType: "",
    });
    setPendingPricings([]);
    setPendingFeatures([]);
    setNewPricingBillingCycleId("");
    setNewPricingCountryCode("RW");
    setNewPricingAmount("");
    setNewPricingCurrency("RWF");
    setNewFeatureCode("");
    setCreatedPlanId(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async () => {
    if (!formData.businessType) return;
    try {
      let planId = createdPlanId;
      if (!planId) {
        const createRes = await createPlan.mutateAsync({
          name: formData.name.trim(),
          description: formData.description?.trim() || undefined,
          isActive: formData.isActive,
          businessType: formData.businessType,
        });
        planId = createRes.createPlan?.plan?.planId ?? null;
        if (!planId) {
          return;
        }
        setCreatedPlanId(planId);
      }

      const pricingsToCreate = [...pendingPricings];
      for (const p of pricingsToCreate) {
        const pricingRes = await createPlanPricing.mutateAsync({
          planId,
          billingCycleId: p.billingCycleId,
          priceAmount: p.priceAmount,
          priceCurrency: p.priceCurrency,
          countryCode: p.countryCode,
        });
        if (!pricingRes.createPlanPricing?.planPricing) {
          return;
        }
        setPendingPricings((prev) =>
          prev.filter(
            (x) =>
              !(
                x.billingCycleId === p.billingCycleId &&
                x.countryCode === p.countryCode
              ),
          ),
        );
      }

      const featuresToAdd = [...pendingFeatures];
      for (const f of featuresToAdd) {
        await addPlanFeature.mutateAsync({
          planId,
          featureCode: f.featureCode,
        });
        setPendingFeatures((prev) =>
          prev.filter((x) => x.featureCode !== f.featureCode),
        );
      }

      onSuccess?.();
      handleClose();
    } catch {
      // Error handled by fetchGraphQL / setAlert; keep createdPlanId for retry
    }
  };

  const isSubmitting =
    createPlan.isPending ||
    createPlanPricing.isPending ||
    addPlanFeature.isPending;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h6" fontWeight="bold">
          Create New Plan
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3, mt: 2 }}>
          <TextField
            label="Plan Name"
            value={formData.name}
            onChange={handleChange("name")}
            fullWidth
            required
            placeholder="e.g., Basic Plan, Premium Plan"
          />
          <TextField
            label="Description"
            value={formData.description}
            onChange={handleChange("description")}
            fullWidth
            multiline
            rows={3}
            placeholder="Describe what this plan includes..."
          />
          <FormControl fullWidth required>
            <InputLabel>Business type</InputLabel>
            <Select
              value={formData.businessType}
              label="Business type"
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  businessType: e.target.value as PlanFormData["businessType"],
                }))
              }
            >
              {BUSINESS_TYPE_OPTIONS.map(({ value, label }) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Switch
                checked={formData.isActive}
                onChange={handleChange("isActive")}
                color="primary"
              />
            }
            label="Active"
          />

          <Typography variant="subtitle2" fontWeight={600}>
            Plan pricings
          </Typography>
          {pendingPricings.length > 0 && (
            <Box display="flex" flexWrap="wrap" gap={1}>
              {pendingPricings.map((p) => (
                <Chip
                  key={`${p.billingCycleId}-${p.countryCode}`}
                  label={`${p.billingCycleCode} · ${p.countryCode}: ${p.priceAmount} ${p.priceCurrency}`}
                  size="small"
                  variant="outlined"
                  onDelete={() =>
                    handleRemovePricing(p.billingCycleId, p.countryCode)
                  }
                  deleteIcon={<Delete fontSize="small" />}
                />
              ))}
            </Box>
          )}
          {canAddMorePricing && (
            <Box display="flex" flexWrap="wrap" gap={1} alignItems="center">
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Billing cycle</InputLabel>
                <Select
                  value={newPricingBillingCycleId}
                  label="Billing cycle"
                  onChange={(e) => {
                    const cycleId = e.target.value;
                    setNewPricingBillingCycleId(cycleId);
                    const available = ADMIN_MARKET_COUNTRIES.filter(
                      (c) =>
                        !pendingPricings.some(
                          (p) =>
                            p.billingCycleId === cycleId &&
                            p.countryCode === c.code,
                        ),
                    );
                    const nextCountry =
                      available.find((c) => c.code === newPricingCountryCode)
                        ?.code ?? available[0]?.code;
                    if (nextCountry) {
                      setNewPricingCountryCode(nextCountry);
                      setNewPricingCurrency(
                        currencyForAdminCountry(nextCountry),
                      );
                    }
                  }}
                >
                  {billingCycles.map((bc) => (
                    <MenuItem key={bc.billingCycleId} value={bc.billingCycleId}>
                      {bc.label} ({bc.code})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Country</InputLabel>
                <Select
                  value={newPricingCountryCode}
                  label="Country"
                  onChange={(e) => handleCountryChange(e.target.value)}
                >
                  {(newPricingBillingCycleId
                    ? countriesAvailableForSelectedCycle
                    : ADMIN_MARKET_COUNTRIES
                  ).map((c) => (
                    <MenuItem key={c.code} value={c.code}>
                      {c.label} ({c.code})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                size="small"
                label="Amount"
                type="number"
                inputProps={{ min: 0, step: 0.01 }}
                value={newPricingAmount}
                onChange={(e) => setNewPricingAmount(e.target.value)}
                sx={{ width: 120 }}
              />
              <TextField
                size="small"
                label="Currency"
                value={newPricingCurrency}
                InputProps={{ readOnly: true }}
                helperText="From country"
                sx={{ width: 100 }}
              />
              <Button
                size="small"
                variant="outlined"
                startIcon={<Add />}
                onClick={handleAddPricing}
                disabled={Boolean(
                  !newPricingBillingCycleId ||
                  !newPricingCountryCode ||
                  !newPricingAmount.trim() ||
                  (newPricingBillingCycleId !== "" &&
                    countriesAvailableForSelectedCycle.length === 0),
                )}
              >
                Add pricing
              </Button>
            </Box>
          )}

          <Typography variant="subtitle2" fontWeight={600}>
            Plan features
          </Typography>
          {pendingFeatures.length > 0 && (
            <Box display="flex" flexWrap="wrap" gap={1}>
              {pendingFeatures.map((f) => (
                <Chip
                  key={f.featureCode}
                  label={f.name}
                  size="small"
                  variant="outlined"
                  onDelete={() => handleRemoveFeature(f.featureCode)}
                  deleteIcon={<Delete fontSize="small" />}
                />
              ))}
            </Box>
          )}
          {featuresNotAdded.length > 0 && (
            <Box display="flex" flexWrap="wrap" gap={1} alignItems="center">
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Feature</InputLabel>
                <Select
                  value={newFeatureCode}
                  label="Feature"
                  onChange={(e) => setNewFeatureCode(e.target.value)}
                >
                  {featuresNotAdded.map((f) => (
                    <MenuItem key={f.featureCode} value={f.featureCode}>
                      {f.name} ({f.featureCode})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                size="small"
                variant="outlined"
                startIcon={<Add />}
                onClick={handleAddFeature}
                disabled={!newFeatureCode}
              >
                Add feature
              </Button>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={
            !formData.name.trim() || !formData.businessType || isSubmitting
          }
          sx={{
            backgroundColor: BRAND_COLOR,
            "&:hover": {
              backgroundColor: BRAND_HOVER,
            },
          }}
        >
          {isSubmitting ? "Creating…" : createdPlanId ? "Retry" : "Create Plan"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddPlanModal;
